/**
 * Whether the API is reachable (P0-WEB-04, PRD §15 "network loss mid-session").
 *
 * `navigator.onLine` alone is not enough to drive a banner. It reports whether
 * the machine has *a* network, so it stays `true` on a café Wi-Fi that has
 * stopped routing and on a laptop whose VPN dropped — exactly the cases where
 * the user needs telling. It is also `true` when the API itself is down.
 *
 * So this combines both signals: the browser's event, and what requests
 * actually did. A transport-level failure marks the API unreachable; any
 * response at all — including a 500, which proves something answered — marks it
 * reachable again.
 */

export type ConnectivityListener = (reachable: boolean) => void;

/**
 * Declared as function-valued properties rather than methods on purpose.
 *
 * `useSyncExternalStore` is handed `store.subscribe` and `store.getSnapshot`
 * detached from the object, which is exactly the mistake
 * `@typescript-eslint/unbound-method` exists to catch. Property syntax says
 * these never rely on `this`, which is true — the factory closes over its
 * state — and makes passing them around correct rather than merely tolerated.
 */
export interface ConnectivityStore {
  /** For `useSyncExternalStore`. Returns an unsubscribe. */
  readonly subscribe: (listener: ConnectivityListener) => () => void;
  /** Identity-stable while nothing changes, as `useSyncExternalStore` requires. */
  readonly getSnapshot: () => boolean;
  /** A request got a response. */
  readonly markReachable: () => void;
  /** A request failed at the transport level. */
  readonly markUnreachable: () => void;
}

export function createConnectivityStore(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> | undefined = globalThis.window,
  navigatorRef: { onLine: boolean } | undefined = globalThis.navigator,
): ConnectivityStore {
  // Optimistic on first paint. Starting "offline" would flash a banner on every
  // cold load until the first request answered, and a banner that cries wolf
  // once is ignored the time it is right.
  let reachable = navigatorRef?.onLine ?? true;
  const listeners = new Set<ConnectivityListener>();

  function set(next: boolean): void {
    if (next === reachable) return;
    reachable = next;
    for (const listener of listeners) listener(reachable);
  }

  // The browser going offline is authoritative in one direction only: no
  // network means nothing can reach the API. It coming back does not mean the
  // API answers, so `online` only clears the flag pending the next request.
  const onOffline = () => {
    set(false);
  };
  const onOnline = () => {
    set(true);
  };

  target?.addEventListener('offline', onOffline);
  target?.addEventListener('online', onOnline);

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return reachable;
    },
    markReachable() {
      set(true);
    },
    markUnreachable() {
      set(false);
    },
  };
}
